import { ActionIcon, Badge, Card, Button, Group, Select, Stack, Table } from "@mantine/core";
import { MonthPickerInput } from "@mantine/dates";
import { IconEye, IconPlayerPlay, IconReceiptOff } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import useSWR from "swr";
import { EndCustomerChargeApi } from "../../../apis";
import type { EcCharge, EcChargeStatus } from "../../../apis/endCustomerCharge";
import { BackfillRunModal } from "../../../components/billing/BackfillRunModal";
import { EcChargeDetailModal } from "../../../components/billing/EcChargeDetailModal";
import { EmptyState } from "../../../components/billing/EmptyState";
import { MoneyCell } from "../../../components/billing/MoneyCell";
import { PermissionGate } from "../../../components/billing/PermissionGate";
import { ShortId } from "../../../components/billing/ShortId";
import { TenantName } from "../../../components/billing/TenantName";
import { TenantSelect } from "../../../components/billing/TenantSelect";
import { PageLayout } from "../../../components/layout";
import { formatDate } from "../../../utils/format";

const breadcrumb = [
  { to: "/", label: "Эхлэл" },
  { to: "/billing", label: "Билл" },
  { label: "EC шимтгэл" },
];

const STATUS_COLORS: Record<string, string> = {
  PENDING              : "gray",
  INVOICED             : "blue",
  PAID                 : "green",
  OVERDUE              : "orange",
  FALLBACK_TRANSFERRED : "red",
  WAIVED               : "gray",
};

const STATUS_LABEL: Record<string, string> = {
  PENDING              : "Хүлээгдэж буй",
  INVOICED             : "Нэхэмжилсэн",
  PAID                 : "Төлсөн",
  OVERDUE              : "Хугацаа хэтэрсэн",
  FALLBACK_TRANSFERRED : "Тенант рүү шилжүүлсэн",
  WAIVED               : "Хүчингүй",
};

const MODE_LABEL: Record<string, string> = {
  VIA_TENANT                  : "Тенантаар",
  DIRECT_FROM_CUSTOMER        : "Харилцагчаас шууд",
  DIRECT_WITH_TENANT_FALLBACK : "Шууд (нөөц: тенант)",
};

const STATUS_OPTIONS = Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }));

const EcChargesList = () => {
  const [tenantFilter, setTenantFilter]     = useState("");
  const [periodFilter, setPeriodFilter]     = useState("");
  const [statusFilter, setStatusFilter]     = useState<EcChargeStatus | null>(null);
  const [detailOpen, setDetailOpen]         = useState(false);
  const [backfillOpen, setBackfillOpen]     = useState(false);
  const [selected, setSelected]             = useState<EcCharge | null>(null);

  const swrKey = useMemo(
    () => `swr.admin.ec-charges.${tenantFilter}.${periodFilter}.${statusFilter}`,
    [tenantFilter, periodFilter, statusFilter]
  );
  const { data, mutate } = useSWR(swrKey, () =>
    EndCustomerChargeApi.listAdmin({
      tenantId          : tenantFilter || undefined,
      period            : periodFilter || undefined,
      status            : statusFilter ?? undefined,
      page              : 1,
      limit             : 200,
    })
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw  = data as any;
  const rows: EcCharge[] = Array.isArray(raw) ? raw : (raw?.rows ?? []);

  const openDetail = (c: EcCharge) => {
    setSelected(c);
    setDetailOpen(true);
  };

  return (
    <PageLayout
      title="EC шимтгэл"
      subTitle="End-customer recurring шимтгэлийн жагсаалт"
      breadcrumb={breadcrumb}
      extra={[
        <PermissionGate key="backfill" code="BIL_020">
          <Button
            leftSection={<IconPlayerPlay size={16} />}
            variant="outline"
            onClick={() => setBackfillOpen(true)}>
            Гараар үүсгэх
          </Button>
        </PermissionGate>,
      ]}>
      <Stack gap="md">
        <Group gap="sm" wrap="wrap">
          <TenantSelect value={tenantFilter} onChange={setTenantFilter} placeholder="Бүх тенант" width={260} />
          <MonthPickerInput
            placeholder="Тооцооны үе"
            value={periodFilter ? `${periodFilter}-01` : null}
            onChange={(v) => setPeriodFilter(v ? String(v).slice(0, 7) : "")}
            valueFormat="YYYY-MM"
            clearable
            w={180}
          />
          <Select
            placeholder="Бүх төлөв"
            clearable
            w={200}
            data={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => setStatusFilter((v as EcChargeStatus) || null)}
          />
        </Group>

        <Card withBorder radius="md" padding={rows.length === 0 ? "md" : 0}>
          {rows.length === 0 ? (
            <EmptyState icon={<IconReceiptOff size={26} />} message="Шимтгэл байхгүй." />
          ) : (
            <Table.ScrollContainer minWidth={860}>
              <Table verticalSpacing="sm" highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Тенант</Table.Th>
                    <Table.Th>Харилцагч</Table.Th>
                    <Table.Th>Тооцооны үе</Table.Th>
                    <Table.Th ta="right">Дүн</Table.Th>
                    <Table.Th>Цуглуулах арга</Table.Th>
                    <Table.Th>Төлөв</Table.Th>
                    <Table.Th>Төлөх огноо</Table.Th>
                    <Table.Th w={48}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((c) => (
                    <Table.Tr key={c.id}>
                      <Table.Td><TenantName id={c.tenantId} /></Table.Td>
                      <Table.Td><ShortId id={c.endCustomerBusinessId} /></Table.Td>
                      <Table.Td style={{ fontVariantNumeric: "tabular-nums" }}>{c.period}</Table.Td>
                      <Table.Td ta="right"><MoneyCell value={c.amount} currency={c.currency} /></Table.Td>
                      <Table.Td>
                        <Badge color="gray" variant="light" radius="sm">
                          {MODE_LABEL[c.collectionMode] ?? c.collectionMode}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={STATUS_COLORS[c.status]} variant="light" radius="sm">
                          {STATUS_LABEL[c.status] ?? c.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td style={{ fontVariantNumeric: "tabular-nums" }}>{formatDate(c.dueDate)}</Table.Td>
                      <Table.Td>
                        <ActionIcon variant="subtle" color="gray" onClick={() => openDetail(c)} aria-label="Дэлгэрэнгүй">
                          <IconEye size={16} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Card>

        <EcChargeDetailModal
          opened={detailOpen}
          onClose={() => {
            setDetailOpen(false);
            setSelected(null);
          }}
          charge={selected}
          onMutated={() => mutate()}
        />
        <BackfillRunModal
          opened={backfillOpen}
          onClose={() => setBackfillOpen(false)}
          onSuccess={() => mutate()}
        />
      </Stack>
    </PageLayout>
  );
};

export default EcChargesList;
